import { memo, useMemo, useState } from "react";
import { motion } from "framer-motion";
import {
  Wrench, ChevronDown, ChevronRight, Search, Zap, FileEdit,
  Terminal, FileText,
  RefreshCw, Copy, Check, Volume2, Square,
} from "lucide-react";
import { cn } from "../../lib/cn";
import { StreamingText } from "./StreamingText";
import { FileDownloadCards } from "./FileDownloadCards";
import { UserAttachmentChips, splitUserMessageAttachments } from "./UserAttachmentChips";
import { useChatsStore, type ChatMessage } from "../../store/chats";
import { useSettingsStore } from "../../store/settings";
import { useChat } from "../../hooks/useChat";
import { useVoice } from "../../hooks/useVoice";
import { unlockAudio } from "../../lib/ttsQueue";
import { AvatarBubble } from "../ui/AvatarBubble";
import { getProfile } from "../../lib/storage";
import { simplifyForUser, isContentEmptyForSimple } from "../../lib/simplifyForUser";
import { ConfirmCard, extractConfirmId, extractConfirmPreview } from "./ConfirmCard";
import { ActionLog } from "./ActionLog";

export const MessageBubble = memo(function MessageBubble({ message }: { message: ChatMessage }) {
  const mode = useSettingsStore((s) => s.displayMode);
  // Время рендера используем как приближение «когда сообщение появилось».
  // ChatMessage сейчас не имеет timestamp в store (мы это не меняем); показ
  // относительный («только что», «5 мин») вычисляется на лету.
  const [shownAt] = useState(() => Date.now());

  if (message.role === "user") {
    const profile = getProfile<{ username?: string; email?: string }>();
    const userLabel = profile?.username || profile?.email || "U";
    const { cleanText, chips } = splitUserMessageAttachments(message.text);
    return (
      <motion.div
        initial={{ opacity: 0, y: 4 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.15 }}
        className="group flex justify-end items-end gap-2 sm:gap-3"
      >
        <div className="flex flex-col items-end gap-1 max-w-[90%] sm:max-w-[82%] md:max-w-[68%]">
          <RelativeTime shownAt={shownAt} alignRight />
          <div
            className="rounded-2xl rounded-tr-md px-4 py-2.5 text-white shadow-accent text-body leading-relaxed"
            style={{ backgroundImage: "var(--grad-cta)" }}
          >
            {cleanText && <StreamingText text={cleanText} className="text-white" />}
            <UserAttachmentChips chips={chips} />
          </div>
        </div>
        <AvatarBubble kind="user" label={userLabel} size="sm" className="hidden sm:inline-flex" />
      </motion.div>
    );
  }

  // === SIMPLE MODE: only final text ===
  if (mode === "chat") {
    const actions = message.actions || [];
    const hasActions = actions.length > 0;

    // Во время streaming без текста и без actions — не показываем ничего
    // (StatusIndicator покажет "Анализирую запрос...")
    if (message.streaming && !message.text && !hasActions) {
      return null;
    }
    if (!message.text && !hasActions) return null;

    const displayText = simplifyForUser(message.text);
    const confirmId = !message.streaming ? extractConfirmId(message.text) : null;

    // Если нет ни текста, ни actions, ни confirm — скрываем
    if (!displayText && !confirmId && !hasActions) {
      if (message.streaming) return null;
      if (isContentEmptyForSimple(message.text)) return null;
    }

    // Если есть только actions без текста — показываем только ActionLog
    const showCard = !!(displayText || confirmId);

    return (
      <motion.div
        initial={{ opacity: 0, y: 4 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.15 }}
        className="group flex justify-start items-start gap-2 sm:gap-3"
      >
        <AvatarBubble kind="ai" size="sm" className="hidden sm:inline-flex shrink-0 mt-0.5" />
        <div className="flex flex-col items-start gap-1 max-w-full sm:max-w-[92%] md:max-w-[82%]">
          <RelativeTime shownAt={shownAt} />
          {/* Thinking block — collapsible */}
          {hasActions && message.thinking && (
            <ThinkingBlock text={message.thinking} />
          )}
          {/* Action log — always visible, persists in history */}
          {hasActions && <ActionLog actions={actions} streaming={!!message.streaming} />}
          {/* Text card — only if there's actual content */}
          {showCard && (
            <div className="card p-4 sm:p-5 relative w-full">
              {displayText && <StreamingText text={displayText} streaming={message.streaming} />}
              {confirmId && (
                <ConfirmCard
                  confirmId={confirmId}
                  preview={extractConfirmPreview(message.text)}
                />
              )}
              {!message.streaming && <FileDownloadCards text={displayText} />}
              {!message.streaming && <MessageActions message={message} />}
            </div>
          )}
        </div>
      </motion.div>
    );
  }

  // === DEVELOPER MODE: full action cards + text ===
  const actions = message.actions || [];
  return (
    <motion.div
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.15 }}
      className="group flex justify-start items-start gap-2 sm:gap-3"
    >
      <AvatarBubble kind="ai" size="sm" className="hidden sm:inline-flex shrink-0 mt-0.5" />
      <div className="flex flex-col items-start gap-1.5 max-w-full sm:max-w-[92%] md:max-w-[86%] flex-1 min-w-0">
        <RelativeTime shownAt={shownAt} />
        <div className="space-y-0.5 w-full">
          {actions.map((a, i) => (
            <CollapsibleAction key={i} {...a} />
          ))}
          {message.text && (
            <div className="card p-4 relative">
              <StreamingText text={message.text} streaming={message.streaming} />
              {!message.streaming && (() => {
                const cid = extractConfirmId(message.text);
                if (!cid) return null;
                return (
                  <ConfirmCard
                    confirmId={cid}
                    preview={extractConfirmPreview(message.text)}
                  />
                );
              })()}
              {!message.streaming && <FileDownloadCards text={message.text} />}
              {!message.streaming && <MessageActions message={message} />}
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
});

// ─── Relative time on hover ────────────────────────────────────────────────

function RelativeTime({ shownAt, alignRight }: { shownAt: number; alignRight?: boolean }) {
  // Обновлять метку на rerender сообщения нет смысла — но можно дать живую
  // относительную метку через redraw. Делаем компромисс — показ on-hover группы.
  const label = useMemo(() => formatRelative(Date.now() - shownAt), [shownAt]);
  return (
    <span
      className={cn(
        "text-[10px] uppercase tracking-wider font-mono text-fg-subtle px-1",
        "opacity-0 group-hover:opacity-100 transition-opacity",
        alignRight && "self-end",
      )}
    >
      {label}
    </span>
  );
}

function formatRelative(ms: number): string {
  if (ms < 5_000) return "только что";
  if (ms < 60_000) return `${Math.floor(ms / 1000)} сек назад`;
  if (ms < 3_600_000) return `${Math.floor(ms / 60_000)} мин назад`;
  return `${Math.floor(ms / 3_600_000)} ч назад`;
}

// ─── Action buttons over assistant message ─────────────────────────────────

function MessageActions({ message }: { message: ChatMessage }) {
  const [copied, setCopied] = useState(false);
  const mode = useSettingsStore((s) => s.displayMode);
  const sessions = useChatsStore((s) => s.sessions);
  const activeId = useChatsStore((s) => s.activeId);
  const { send, streaming } = useChat();
  const { state: voiceState, speak, stopSpeaking } = useVoice();
  const isSpeaking = voiceState === "speaking";

  // В simple mode копируем и озвучиваем уже отфильтрованный текст
  // (без «Краткий обзор», без /root/... путей), чтобы пользователь
  // получил тот же чистый ответ, что видит в чате.
  const textForActions = mode === "chat" ? simplifyForUser(message.text) : message.text;

  function handleCopy() {
    navigator.clipboard.writeText(textForActions).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  }

  function handleRetry() {
    if (streaming) return;
    const session = sessions.find((s) => s.id === activeId);
    if (!session) return;
    const idx = session.messages.findIndex((m) => m.id === message.id);
    if (idx <= 0) return;
    for (let i = idx - 1; i >= 0; i--) {
      const m = session.messages[i];
      if (m.role === "user" && m.text.trim()) {
        send(m.text);
        return;
      }
    }
  }

  function handleSpeak() {
    if (isSpeaking) {
      stopSpeaking();
      return;
    }
    if (!textForActions.trim()) return;
    unlockAudio();
    void speak(textForActions);
  }

  return (
    <div className="absolute top-1.5 right-1.5 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
      <button
        onClick={handleSpeak}
        className={cn(
          "p-1.5 rounded-md bg-bg/80 hover:bg-bg-subtle border border-border/40",
          isSpeaking ? "text-accent-500" : "text-fg-muted hover:text-accent-500",
        )}
        title={isSpeaking ? "Остановить озвучку" : "Озвучить голосом Ruslan"}
        aria-label={isSpeaking ? "Остановить" : "Озвучить"}
      >
        {isSpeaking ? <Square className="size-3.5" /> : <Volume2 className="size-3.5" />}
      </button>
      <button
        onClick={handleRetry}
        disabled={streaming}
        className="p-1.5 rounded-md bg-bg/80 hover:bg-bg-subtle text-fg-muted hover:text-accent-500 disabled:opacity-40 disabled:cursor-not-allowed border border-border/40"
        title="Переотправить запрос"
        aria-label="Переотправить"
      >
        <RefreshCw className="size-3.5" />
      </button>
      <button
        onClick={handleCopy}
        className="p-1.5 rounded-md bg-bg/80 hover:bg-bg-subtle text-fg-muted hover:text-accent-500 border border-border/40"
        title="Скопировать текст"
        aria-label="Копировать"
      >
        {copied ? <Check className="size-3.5 text-success" /> : <Copy className="size-3.5" />}
      </button>
    </div>
  );
}

// ─── Action card (developer mode) ──────────────────────────────────────────

function CollapsibleAction({
  name,
  args,
  result,
  pre,
  post,
}: {
  name: string;
  args?: Record<string, unknown>;
  result?: string;
  pre?: string;
  post?: string;
}) {
  const [open, setOpen] = useState(false);
  const Icon = getToolIcon(name);
  const summary = summarizeArgs(args);
  const hasError = result && /^(❌|⚠️|error|ошибка)/i.test(result.slice(0, 20));

  return (
    <div className="group/action">
      {/* Compact inline row — no card, no padding bloat */}
      <button
        onClick={() => result && setOpen(!open)}
        className={cn(
          "w-full flex items-center gap-1.5 px-2 py-0.5 rounded text-left",
          "hover:bg-bg-subtle/60 transition-colors text-[0.8rem] leading-tight",
          hasError && "text-red-400",
        )}
      >
        <Icon className="size-3.5 shrink-0 text-fg-muted" />
        <span className="font-mono text-fg-muted">{name}</span>
        {summary && (
          <span className="font-mono text-fg-subtle truncate flex-1 min-w-0 opacity-70">
            {summary}
          </span>
        )}
        {result && (
          open
            ? <ChevronDown className="size-3 shrink-0 text-fg-subtle opacity-0 group-hover/action:opacity-100" />
            : <ChevronRight className="size-3 shrink-0 text-fg-subtle opacity-0 group-hover/action:opacity-100" />
        )}
      </button>
      {open && result && (
        <motion.pre
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          className="ml-6 px-2 py-1 rounded bg-bg-subtle/40 text-[0.75rem] text-fg-muted whitespace-pre-wrap max-h-40 overflow-y-auto border-l-2 border-border/30"
        >
          {result.slice(0, 2000)}
          {result.length > 2000 && "\n…"}
        </motion.pre>
      )}
      {/* Post-action: only show errors (backend now only sends errors) */}
      {post && <span className="ml-6 text-[0.75rem] text-red-400">{post}</span>}
    </div>
  );
}

function getToolIcon(name: string) {
  if (name.includes("terminal") || name.includes("run") || name.includes("shell")) return Terminal;
  if (name.includes("read") || name.includes("search") || name.includes("find")) return Search;
  if (name.includes("write") || name.includes("edit") || name.includes("create")) return FileEdit;
  if (name.includes("file")) return FileText;
  if (name.includes("exec")) return Zap;
  return Wrench;
}

function summarizeArgs(args?: Record<string, unknown>): string {
  if (!args) return "";
  const path = args.path ?? args.file ?? args.command ?? args.query;
  if (typeof path === "string") return path.length > 60 ? path.slice(0, 57) + "…" : path;
  try {
    const j = JSON.stringify(args);
    return j.length > 60 ? j.slice(0, 57) + "…" : j;
  } catch { return ""; }
}


// ─── Thinking indicator (Сессия 14) ────────────────────────────────────────

/**
 * ThinkingDots — три точки с разной фазой пульсации, brand-blue.
 * Показывается в Simple mode пока агент думает (streaming без текста).
 */
function ThinkingBlock({ text }: { text: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="mb-1">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1.5 text-[0.72rem] text-fg-subtle hover:text-fg-muted transition-colors"
      >
        <span className={`transition-transform ${open ? "rotate-90" : ""}`}>▶</span>
        <span>Thinking</span>
        <span className="text-fg-subtle/60">({text.length} chars)</span>
      </button>
      {open && (
        <pre className="mt-1 ml-4 text-[0.68rem] text-fg-subtle/80 font-mono whitespace-pre-wrap max-h-[200px] overflow-y-auto bg-bg-subtle/30 rounded p-2">
          {text.slice(0, 2000)}
        </pre>
      )}
    </div>
  );
}

function ThinkingDots() {
  return (
    <div className="flex items-center gap-1" aria-label="Агент анализирует">
      <span
        className="size-1.5 rounded-full bg-accent-500 animate-pulse"
        style={{ animationDelay: "0ms", animationDuration: "1200ms" }}
      />
      <span
        className="size-1.5 rounded-full bg-accent-500 animate-pulse"
        style={{ animationDelay: "200ms", animationDuration: "1200ms" }}
      />
      <span
        className="size-1.5 rounded-full bg-accent-500 animate-pulse"
        style={{ animationDelay: "400ms", animationDuration: "1200ms" }}
      />
    </div>
  );
}
